/**
 * VAL-68-OWNER-RULINGS R2 — "The buyer can always ask for an upgrade, add options."
 *
 * After buying, "Your value" lists the higher options not yet bought (server: GET /payment
 * `upgrades`, lowest first, each with its charge from core/valuation/value_payment.
 * upgrade_charge_cents). Each button starts a normal Stripe checkout for that option; the
 * server refuses an option already held. Prices are TEST values, never presented as final.
 * Renders nothing while there is nothing to add (or the list cannot be read).
 *
 * ADD_TEXT is WORDING FOR OWNER APPROVAL (VAL-68-OWNER-RULINGS report).
 *
 * VAL-UPGRADE-CREDIT-1 (owner 2026-10-31 05:58, "mockup A"): the owner pays only the
 * difference. Each option shows Price / Already paid − / You pay, then "Add for $Z"
 * (button wording already approved). The three labels are WORDING FOR OWNER APPROVAL.
 *
 * Owner rulings 2026-10-31 09:19: an option earlier payments cover in full reads "Add for $0.00".
 * The click only ASKS for it (no Stripe, so it works while online payment is off); a super_admin
 * unlocks it on the admin screen. Until then the card shows the report card's existing
 * "Waiting for payment" line instead of the button.
 */
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { fetchPaymentInfo, startCheckout, type PaymentInfo } from '@/api/valuationPayment'
import { formatApiError } from '@/utils/httpErrors'
import { CLIENT_TEXT as PAY_TEXT, PayRedirecting, formatCents, redirectToCheckout } from './_PayForValue'

export const ADD_TEXT = {
  title: 'Add options',
  button: 'Add for {amount}',
  /** The same words _OwnerReportCard shows while a payment is outstanding (not new wording). */
  waiting: 'Waiting for payment',
  // WORDING FOR OWNER APPROVAL (VAL-UPGRADE-CREDIT-1): the three lines of mockup A.
  price: 'Price',
  alreadyPaid: 'Already paid',
  youPay: 'You pay',
} as const

export function AddOptions({ engagementId }: { engagementId: string }) {
  const [info, setInfo] = useState<PaymentInfo | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    fetchPaymentInfo(engagementId)
      .then(res => { if (live) setInfo(res) })
      .catch(() => { /* nothing to add is shown as nothing */ })
    return () => { live = false }
  }, [engagementId])

  const reload = () => fetchPaymentInfo(engagementId).then(setInfo).catch(() => { /* keep what is shown */ })

  const ups = info?.upgrades ?? []
  if (!info || ups.length === 0) return null
  const off = !info.payments_configured

  const add = async (tier: string) => {
    setBusy(tier)
    setError(null)
    try {
      const url = await startCheckout(engagementId, tier)
      if (url) { redirectToCheckout(url); return }  // VAL-70: notice stays up until the browser leaves
      setBusy(null)
      await reload()  // $0: asked for, now waiting for a super_admin
    } catch (err: unknown) {
      setBusy(null)
      setError(formatApiError(err))
    }
  }

  return (
    <Card data-testid="add-options">
      <CardHeader><CardTitle>{ADD_TEXT.title}</CardTitle></CardHeader>
      <CardContent className="space-y-4 text-sm text-pal-ink-body">
        {off && <p className="text-pal-warning-text">{PAY_TEXT['pay.notConfigured']}</p>}
        <div className="flex flex-wrap gap-3">
          {ups.map(o => (
            <div key={o.tier} className="rounded-lg border border-pal-line p-4" data-testid={`add-${o.tier}`}>
              <p className="font-medium text-pal-ink">{o.title ?? o.label}</p>
              {o.description && <p className="mt-1 max-w-xs text-pal-ink-soft">{o.description}</p>}
              {o.price_cents != null && o.already_paid_cents != null && o.already_paid_cents > 0 && (
                <div className="mt-2 space-y-1" data-testid={`add-${o.tier}-lines`}>
                  <p className="flex justify-between gap-4">
                    <span>{ADD_TEXT.price}</span><span>{formatCents(o.price_cents, o.currency)}</span>
                  </p>
                  <p className="flex justify-between gap-4">
                    <span>{ADD_TEXT.alreadyPaid}</span>
                    <span>− {formatCents(o.already_paid_cents, o.currency)}</span>
                  </p>
                  <p className="flex justify-between gap-4 font-medium text-pal-ink">
                    <span>{ADD_TEXT.youPay}</span><span>{formatCents(o.amount_cents, o.currency)}</span>
                  </p>
                </div>
              )}
              {o.pending ? (
                <p className="mt-3 text-sm text-pal-ink-body" data-testid={`add-${o.tier}-waiting`}>{ADD_TEXT.waiting}</p>
              ) : (
                <Button className="mt-3" variant="outline" disabled={(off && o.amount_cents > 0) || busy !== null}
                        onClick={() => { void add(o.tier) }}>
                  {ADD_TEXT.button.replace('{amount}', formatCents(o.amount_cents, o.currency))}
                </Button>
              )}
            </div>
          ))}
        </div>
        {/* VAL-70: a $0 option is only asked for (no Stripe), so it gets no notice. */}
        {ups.some(o => o.tier === busy && o.amount_cents > 0) && <PayRedirecting />}
        {!off && <p className="text-xs text-pal-ink-muted">{PAY_TEXT['pay.secure']}</p>}
        {error && <p className="text-pal-error-text">{error}</p>}
      </CardContent>
    </Card>
  )
}
